import { Storybook } from "@tento-ui/ui/storybook";
import { TurnPrompt } from "../turn/prompt";
import { TurnResult } from "../turn/result";
import { TurnTool, TurnToolGroup } from "../turn/tool";
import { TurnNote } from "../turn/note";
import { AgentTranscript } from "./transcript";
import { TurnBar } from "../turn/bar";

/* Inlined rather than imported: a storybook card is bundled separately, and
 * an `icons/*.svg` import arrives there as a path, not markup — the card then
 * dies setting `slot` on nothing. `turn/bar.example.ts` carries its own for
 * the same reason. */
const COPY_ICON =
  `<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><g fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="5.5" y="5.5" width="8" height="8" rx="1.6"/><path d="M10.5 2.5h-7a1 1 0 0 0-1 1v7"/></g></svg>`;

/** A transcript needs a viewport to virtualize against; the height goes on
 * the component itself rather than a wrapper element. */
function sized(transcript: AgentTranscript, height: string): AgentTranscript {
  transcript.style.height = height;
  return transcript;
}

/** The agent's reply, as a plain measurable block rather than `turn.prose`.
 *
 * A storybook card is bundled on its own, and the prosemirror path map
 * resolves to a declaration file, so `std-prosemirror` never registers and
 * every `turn.prose` inside a card renders zero-height. In a card ABOUT
 * measured row heights that is worse than cosmetic: the rows the reviewer
 * is being asked to judge would be the invisible ones. Same class of
 * hazard as an `icons/*.svg` import in a card. */
function reply(text: string): HTMLElement {
  const $p = document.createElement("div");
  $p.textContent = text;
  $p.style.fontSize = "13px";
  $p.style.lineHeight = "1.5";
  $p.style.padding = "1px 0";
  $p.style.color = "var(--text-pri, #1f2328)";
  return $p;
}

/** Five rows per synthetic turn, in envelope order: prompt, reply, the
 * turn's tool run folded into ONE row, an ending only when there is one,
 * then the trailing bar — attribution closes a turn rather than heading it.
 *
 * Two things here are the point of the card rather than scaffolding.
 *
 * The tool run is a `tool.group`, not four `tool` rows. A turn that reads
 * four files spends four rows saying so, and in a virtualized scrollback
 * that is four rows of someone else's bookkeeping between the question and
 * the answer. Folded, the same run costs one line and still opens. Every
 * fourth turn fails one of its calls, which is the case that proves the
 * group cannot hide one: those open themselves.
 *
 * There is no row for a successful result. `turn.result` draws nothing for
 * `ok`, and a host that measures its rows must not mount one anyway — this
 * host reads a 0 measurement as NOT YET MEASURED (an unmounted cell
 * reports 0 too) and would hold the estimate, leaving a 28px gap where the
 * nothing is. A caller projecting rows from the wire skips the ok. */
function longRun(index: number): HTMLElement {
  const turn = Math.floor(index / 5);
  const failing = turn % 4 === 3;
  switch (index % 5) {
    case 0:
      return TurnPrompt.makeWith({
        text: `unit ${turn}: re-run the failing check and report what moved`,
      });
    case 1:
      return reply("Reading the config first, then the test that covers it.");
    case 2:
      return TurnToolGroup.makeWith({
        tools: [
          {
            tool: "read",
            title: `tento-ui-js/components/turn/row-${turn}.ts`,
            note: "165 lines",
            status: "done",
          },
          {
            tool: "grep",
            title: "renderHarnessView",
            note: "3 hits",
            status: "done",
          },
          {
            tool: "edit",
            title: `tento-ui-js/components/turn/row-${turn}.ts`,
            note: "+12 −4",
            status: "done",
          },
          {
            tool: "execute",
            verb: "Ran",
            title: "deno check html/routes/*.ts",
            note: failing ? "exit 1" : "exit 0",
            status: failing ? "failed" : "done",
            detail: failing
              ? {
                text:
                  "error: TS2345 [ERROR]: Argument of type 'string' is not\nassignable to parameter of type 'HarnessView'.",
              }
              : undefined,
          },
        ],
      });
    case 3:
      // Only a turn that did NOT go fine draws a row here — see above.
      return failing
        ? TurnResult.makeWith({
          status: "error",
          text: `deno check failed — unit ${turn}`,
          detail: "at html/routes/stream-render.ts:90:30",
        })
        : reply("The check passes now; nothing else moved.");
    default:
      return TurnBar.makeWith({
        actions: [{ id: "copy", label: "copy", icon: COPY_ICON }],
        meta: [
          { key: "lane", value: "claude" },
          { key: "unit", value: `u-${turn}` },
          { key: "rows", value: "5" },
          { key: "at", value: `2026-08-06T04:0${turn % 10}:11Z` },
        ],
      });
  }
}

export class AgentTranscriptStoryBook extends Storybook.Default {
  title(): string {
    return "Agent Transcript";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Long run — 600 rows, only the visible ones mount",
        render: () =>
          sized(
            AgentTranscript.makeWith({ count: 600, renderAt: longRun }),
            "320px",
          ),
      },
      {
        title:
          "Expanding a folded run re-measures — the row grows, nothing jumps",
        render: () =>
          sized(
            AgentTranscript.makeWith({
              count: 12,
              renderAt: (index) =>
                index % 3 === 0
                  ? TurnToolGroup.makeWith({
                    tools: [
                      {
                        tool: "read",
                        title: `tento-spool/src/step-${index}.rs`,
                        note: "412 lines",
                        status: "done",
                      },
                      {
                        tool: "execute",
                        verb: "Ran",
                        title: "cargo build -p tento-spool",
                        note: "exit 0",
                        status: "done",
                        detail: {
                          text:
                            "   Compiling tento-spool v0.1.0\n    Finished in 12.44s",
                        },
                      },
                    ],
                  })
                  : reply(
                    `Row ${index} — click a folded run above and watch the rows below it settle rather than jump.`,
                  ),
            }),
            "320px",
          ),
      },
      {
        title: "Rows of wildly different heights",
        render: () =>
          sized(
            AgentTranscript.makeWith({
              count: 40,
              renderAt: (index) =>
                index % 4 === 0
                  ? reply(
                    `Row ${index} is a long paragraph that wraps across several lines so the measured height differs sharply from its neighbours, which is exactly the case a declared row height gets wrong. `
                      .repeat(3),
                  )
                  : TurnTool.makeWith({
                    tool: "execute",
                    title: `step ${index}`,
                    status: "done",
                  }),
            }),
            "320px",
          ),
      },
      {
        title: "Following the tail — a live note is the last row",
        render: () =>
          sized(
            AgentTranscript.makeWith({
              count: 24,
              renderAt: (index) =>
                index === 23
                  ? TurnNote.makeWith({
                    face: "text",
                    text: "Distilling the transcript…",
                    active: true,
                  })
                  : longRun(index),
            }),
            "320px",
          ),
      },
      {
        title: "Empty",
        render: () =>
          sized(
            AgentTranscript.makeWith({
              count: 0,
              renderAt: () => reply(""),
            }),
            "120px",
          ),
      },
    ];
  }
}
